import { useMemo, useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { goldGradientActive } from '@/lib/gradientStyles';

interface Props {
	itemId: string;
	html: string;
	source: 'ai' | 'manual';
	onChange: (html: string, source: 'ai' | 'manual') => void;
	className?: string;
}

const ALLOWED_TAGS_HINT =
	'Allowed tags: <p> <strong> <em> <ul> <ol> <li> <br>. Scripts, iframes, and forms are stripped automatically.';

function buildPreviewDoc(html: string): string {
	return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:12px;background:#fff;color:#1a1a1a;font:14px -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}</style></head><body>${html}</body></html>`;
}

export function EbayDescriptionEditor({ itemId, html, source, onChange, className }: Props) {
	const [tab, setTab] = useState<'edit' | 'preview'>('edit');

	const previewSrc = useMemo(() => buildPreviewDoc(html || '<p style="color:#999">No description yet.</p>'), [html]);

	return (
		<div className={cn('space-y-2', className)}>
			<Label className="text-sm font-medium">eBay Description</Label>

			<div className="flex gap-2 rounded-md border border-caution/25 bg-caution/10 px-3 py-2 text-[11px] leading-snug text-caution">
				<AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-caution" />
				<p>
					<span className="font-semibold uppercase tracking-wide text-caution mr-1">Heads up —</span>
					Descriptions matter. Add a few specific notes about wear, centering, edges, or surface so it matches the photos you uploaded. Your uploaded photos are sent to eBay automatically.
				</p>
			</div>

			<div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-white/[0.05] border border-white/[0.08]">
				<button
					type="button"
					onClick={() => setTab('edit')}
					className={cn(
						'h-8 rounded-full text-xs font-medium transition',
						tab === 'edit' ? goldGradientActive : 'text-muted-foreground hover:text-foreground'
					)}
				>
					Edit HTML
				</button>
				<button
					type="button"
					onClick={() => setTab('preview')}
					className={cn(
						'h-8 rounded-full text-xs font-medium transition',
						tab === 'preview' ? goldGradientActive : 'text-muted-foreground hover:text-foreground'
					)}
				>
					Preview
				</button>
			</div>

			{tab === 'edit' ? (
				<>
					<Textarea
						value={html}
						onChange={(e) => onChange(e.target.value, 'manual')}
						placeholder="Paste or edit your description HTML…"
						rows={8}
						className="font-mono text-xs"
					/>
					<p className="text-[10px] text-muted-foreground">{ALLOWED_TAGS_HINT}</p>
				</>
			) : (
				<div className="rounded-md border border-white/[0.08] bg-white overflow-hidden">
					<iframe
						title="eBay description preview"
						sandbox=""
						srcDoc={previewSrc}
						className="w-full h-64 bg-white"
					/>
				</div>
			)}
		</div>
	);
}

